'use client';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import useOfferComparisonStore, {
  selectSavingsAmount,
} from '../store/use-offer-comparison-store';
import SavingsEquivalentCard from './savings-equivalent-card';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import Image from 'next/image';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { SavingsItem } from '../types/savings-item-type';
import { useMediaQuery } from 'usehooks-ts';
import { formatPrice } from '../utils/format-price';

export default function SavingsEquivalent({
  content,
}: {
  content: SavingsItem[];
}) {
  const t = useTranslations('savingsEquivalent');
  const savings = useOfferComparisonStore(selectSavingsAmount);
  const [activeIndex, setActiveIndex] = useState(0);
  const [items, setItems] = useState<SavingsItem[]>([]);

  const isWiderMobile = useMediaQuery('(min-width:500px)');

  const savingsItems = content.filter((item) => {
    return item.price <= (savings ?? 0);
  });

  useEffect(() => {
    if (savingsItems.length < 4) return;
    const shuffledItems = shuffleItems(savingsItems);
    setItems(shuffledItems.slice(0, 4));
  }, [savings]);

  if (!savings || savingsItems.length < 4) return;

  return (
    <div>
      <p
        data-testid="savings-equivalent-title"
        className="text-center md:px-[51px] pt-10 md:pt-[120px] text-2xl md:text-[42px] leading-9 md:leading-[60px] font-heading"
      >
        {t.rich('title', {
          highlight: (chunks) => (
            <span className="bg-[var(--color-span-default)] text-[var(--color-text-on-primary-action)] rounded-lg text-center p-2 mr-2">
              {chunks}
            </span>
          ),
          value: () => (
            <span data-testid="savings-equivalent-value">
              {formatPrice(savings, false)}
            </span>
          ),
        })}
      </p>
      <p
        data-testid="savings-equivalent-disclaimer"
        className="text-center font-medium text-lg py-6 md:pt-4 md:pb-10"
      >
        {t('disclaimer')}
      </p>
      <div className="hidden md:flex flex-row gap-x-5">
        {items.map((item) => {
          const count = Math.floor(savings / item.price);
          const interpolatedBody = item.body.replace(
            '{value}',
            count.toString()
          );
          return (
            <SavingsEquivalentCard
              key={`${item.title}-${item.price}`}
              title={item.title}
              body={interpolatedBody}
              image={item.image}
            />
          );
        })}
      </div>
      <div className="md:hidden">
        <Swiper
          className="relative !z-0"
          modules={[Navigation, Pagination]}
          spaceBetween={20}
          slidesPerView={isWiderMobile ? 2 : 1.3}
          navigation={{
            nextEl: '.custom-next',
            prevEl: '.custom-prev',
          }}
          pagination={{
            el: '.swiper-pagination',
            type: 'bullets',
            bulletElement: 'button',
            clickable: true,
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.realIndex);
          }}
        >
          {items.map((item) => {
            const count = Math.floor(savings / item.price);
            const interpolatedBody = item.body.replace(
              '{value}',
              count.toString()
            );
            return (
              <SwiperSlide key={`${item.title}-${item.price}`}>
                <div className="h-[392px]">
                  <SavingsEquivalentCard
                    title={item.title}
                    body={interpolatedBody}
                    image={item.image}
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <div className="mx-4 xxs:mx-10 mt-6 flex justify-between items-center ">
          <div className="swiper-pagination"></div>
          <div className="flex flex-row gap-x-4">
            <div
              className={`custom-prev flex h-10 w-10 rounded-full ${
                activeIndex == 0
                  ? 'bg-[var(--color-border-default)]'
                  : 'bg-[var(--color-action-primary)]'
              } justify-center`}
            >
              <Image
                src="/images/previous-arrow.svg"
                alt="Previous"
                width={15.18}
                height={15.18}
              />
            </div>
            <div
              className={`custom-next flex h-10 w-10 rounded-full ${
                activeIndex == items.length - 1
                  ? 'bg-[var(--color-border-default)]'
                  : 'bg-[var(--color-action-primary)]'
              } justify-center`}
            >
              <Image
                src="/images/next-arrow.svg"
                alt="Next"
                width={15.18}
                height={15.18}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function shuffleItems(items: SavingsItem[]) {
  const array = [...items];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}
